import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { evaluateAnswer, getTrainingQuestions, manageTrainingRecords, manageContents } from '../lib/api';
import { Brain, Clock, Award, Search, Filter, Play, History, X, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

interface TrainingQuestion {
  id: string;
  title: string;
  content: string;
  content_id?: string;
  thinking_type_tags: string[];
  topic_tags: string[];
  ai_reference_answer: string;
  created_at: string;
}

interface TrainingRecord {
  id: string;
  text_answer: string;
  ai_evaluation: string;
  score: number;
  created_at: string;
  training_questions: {
    title: string;
    content: string;
    ai_reference_answer: string;
  };
}

const Training: React.FC = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<TrainingQuestion | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [trainingHistory, setTrainingHistory] = useState<TrainingRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState<TrainingRecord | null>(null);
  const [answer, setAnswer] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopicTag, setSelectedTopicTag] = useState('');
  const [selectedThinkingTag, setSelectedThinkingTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [aiEvaluation, setAiEvaluation] = useState('');
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [submissionStep, setSubmissionStep] = useState<'idle' | 'submitted' | 'evaluating' | 'completed'>('idle');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [transcribing, setTranscribing] = useState(false);

  // 语音输入功能
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        await transcribeAudio(audioBlob);
        
        // 停止所有音频轨道
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      setAudioChunks([]);
      setIsRecording(true);
      recorder.start();
      
      toast.success('开始录音...');
    } catch (error) {
      console.error('启动录音失败:', error);
      toast.error('无法访问麦克风，请检查权限设置');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
      toast('录音结束，正在转换为文字...');
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-speech-to-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('语音转文字失败');
      }

      const data = await response.json();
      if (data.success && data.text) {
        // 将转换的文字添加到答案中
        setAnswer(prev => prev + (prev ? '\n' : '') + data.text);
        toast.success('语音转换成功！');
      } else {
        throw new Error('语音转换失败');
      }
    } catch (error) {
      console.error('语音转文字失败:', error);
      toast.error('语音转文字失败，请重试');
    } finally {
      setTranscribing(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      // Get the training questions using the public API
      const questionsResponse = await getTrainingQuestions({
        limit: 50,
        page: 1
      });

      if (!questionsResponse.success) {
        throw new Error(questionsResponse.error || '获取训练题目失败');
      }

      // 确保 questionsData 是数组格式
      const questionsData = Array.isArray(questionsResponse.data) ? questionsResponse.data : [];
      
      console.log('获取到的训练题目数据:', questionsData);
      
      setQuestions(questionsData);
    } catch (error) {
      console.error('获取训练题目失败:', error);
      toast.error('获取训练题目失败');
      // 确保在错误情况下也设置为空数组
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingHistory = async (questionId: string) => {
    if (!user) return;
    
    setLoadingHistory(true);
    try {
      const response = await manageTrainingRecords('list', {
        fields: [
          'id', 'text_answer', 'ai_evaluation', 'score', 'created_at'
        ],
        filters: {
          user_id: user.id,
          question_id: questionId
        },
        order: { created_at: 'desc' },
        include_relations: ['training_questions', 'contents']
      });

      if (!response.success) {
        throw new Error(response.error || '获取训练历史失败');
      }

      const data = response.data || [];

      // Process history with content data
      const historyWithContent = data.map((record: any) => {
        const question = record.training_questions;
        const content = record.contents;
        
        let questionContent = question?.title || ''; // Fallback to title

        if (content?.content_data) {
          questionContent = content.content_data;
        }

        return {
          ...record,
          training_questions: {
            ...question,
            content: questionContent,
          },
        };
      });

      setTrainingHistory(historyWithContent);
    } catch (error) {
      console.error('获取训练历史失败:', error);
      toast.error('获取训练历史失败');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    if (selectedQuestion) {
      setShowHistory(true);
      fetchTrainingHistory(selectedQuestion.id);
    }
  };

  const submitAnswer = async () => {
    if (!selectedQuestion || !answer.trim()) return;

    if (!user) {
      toast.error('请先登录后再提交答案');
      return;
    }

    // 第一步：提交答案
    setSubmissionStep('submitted');
    setSubmitting(true);
    
    try {
      // 先保存训练记录（不包含AI评价）
      const recordResponse = await manageTrainingRecords('create', {
        user_id: user.id,
        question_id: selectedQuestion.id,
        text_answer: answer.trim(),
        ai_evaluation: null, // 暂时为空
        score: null, // 暂时为空
      });

      if (!recordResponse.success) {
        throw new Error(recordResponse.error || '保存训练记录失败');
      }

      const recordData = recordResponse.data;

      toast.success('答案提交成功！AI正在评价中...');
      
      // 第二步：AI评价生成中
      setSubmissionStep('evaluating');
      setSubmitting(false);
      
      // 调用AI评价API
      const evaluation = await evaluateAnswer(
        answer.trim(),
        selectedQuestion.title,
        selectedQuestion.ai_reference_answer,
        selectedQuestion.content || selectedQuestion.ai_reference_answer
      );

      // 更新训练记录，添加AI评价结果
      const updateResponse = await manageTrainingRecords('update', {
        id: recordData.id,
        ai_evaluation: evaluation.evaluation || '评价生成失败',
        score: evaluation.score || 0,
      });

      if (!updateResponse.success) {
        throw new Error(updateResponse.error || '更新训练记录失败');
      }

      setEvaluationData(evaluation);
      setAiEvaluation(evaluation.evaluation || '评价生成失败');
      
      setSubmissionStep('completed');
      setShowReference(true);
      toast.success('AI评价完成！');
    } catch (error) {
      console.error('提交答案失败:', error);
      if (submissionStep === 'submitted') {
        toast.error('提交答案失败');
      } else {
        toast.error('AI评价失败，但答案已保存');
      }
      setSubmissionStep('idle');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuestion = () => {
    setSelectedQuestion(null);
    setAnswer('');
    setShowReference(false);
    setAiEvaluation('');
    setEvaluationData(null);
    setSubmissionStep('idle');
  };

  const filteredQuestions = Array.isArray(questions) ? questions.filter(question => {
    const matchesSearch = question.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTopicTag = selectedTopicTag === '' || question.topic_tags.includes(selectedTopicTag);
    const matchesThinkingTag = selectedThinkingTag === '' || question.thinking_type_tags.includes(selectedThinkingTag);
    return matchesSearch && matchesTopicTag && matchesThinkingTag;
  }) : [];

  const allTopicTags = Array.from(new Set(Array.isArray(questions) ? questions.flatMap(q => q.topic_tags) : []));
  const allThinkingTags = Array.from(new Set(Array.isArray(questions) ? questions.flatMap(q => q.thinking_type_tags) : []));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">热点演练</h1>
          <p className="text-lg text-gray-600 mb-6">
            通过热点话题练习提升思维能力
          </p>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索训练题目..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-4">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedTopicTag}
                  onChange={(e) => setSelectedTopicTag(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white min-w-[120px]"
                >
                  <option value="">主题标签</option>
                  {allTopicTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedThinkingTag}
                  onChange={(e) => setSelectedThinkingTag(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white min-w-[120px]"
                >
                  <option value="">思维类型</option>
                  {allThinkingTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {selectedQuestion ? (
          /* Training Interface */
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedQuestion.title}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {selectedQuestion.topic_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {selectedQuestion.thinking_type_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 text-sm bg-purple-100 text-purple-800 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleShowHistory}
                    className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <History className="w-4 h-4 mr-2" />
                    历史记录
                  </button>
                  <button
                    onClick={resetQuestion}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    返回题目列表
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {!showReference ? (
                /* Answer Input */
                <div>
                  {/* 题目内容显示 */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">📋 题目内容</h4>
                    {/* 题目标题 */}
                    <div className="mb-3 p-3 bg-white rounded-md border border-blue-200">
                      <h5 className="text-sm font-semibold text-blue-900 mb-1">题目标题：</h5>
                      <div className="text-sm text-gray-800 font-medium">
                        {selectedQuestion.title}
                      </div>
                    </div>
                    {/* 题目详细内容 */}
                    <div className="text-sm text-blue-800 whitespace-pre-wrap leading-relaxed">
                      {selectedQuestion.content}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    请基于上述题目内容写下你的思考和答案：
                  </h3>
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="请结合题目要求，详细阐述你的思考过程和解决方案..."
                    className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-gray-500">
                      {answer.length} 字符
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={transcribing}
                        className={`px-4 py-2 text-white rounded-lg transition-colors ${
                          isRecording 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : transcribing
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gray-600 hover:bg-gray-700'
                        }`}
                      >
                        {transcribing ? '转换中...' : isRecording ? '停止录音' : '语音输入'}
                      </button>
                      <button
                        onClick={submitAnswer}
                        disabled={!answer.trim() || submitting || submissionStep !== 'idle'}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        {submitting && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        )}
                        {submissionStep === 'idle' ? '提交答案' : 
                         submissionStep === 'submitted' ? '提交中...' : 
                         submissionStep === 'evaluating' ? 'AI评价中...' : '已完成'}
                      </button>
                    </div>
                  </div>
                  
                  {/* 提交状态提示 */}
                  {submissionStep === 'submitted' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                        <div>
                          <p className="font-medium">答案提交中...</p>
                          <p className="text-sm text-blue-600">正在保存您的答案</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {submissionStep === 'evaluating' && (
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center text-purple-800">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mr-3"></div>
                        <div>
                          <p className="font-medium">AI评价生成中...</p>
                          <p className="text-sm text-purple-600">AI正在分析您的答案，请稍候</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* 录音状态提示 */}
                  {isRecording && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center text-red-800">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-2"></div>
                        <span className="text-sm font-medium">正在录音中... 点击"停止录音"结束</span>
                      </div>
                    </div>
                  )}
                  
                  {transcribing && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-sm font-medium">正在将语音转换为文字...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Results */
                <div className="space-y-6">
                  {/* AI Evaluation */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                      <Brain className="w-5 h-5 mr-2" />
                      AI 评价 {evaluationData?.score && (
                        <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${
                          evaluationData.score >= 90 ? 'bg-green-100 text-green-800' :
                          evaluationData.score >= 80 ? 'bg-blue-100 text-blue-800' :
                          evaluationData.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {evaluationData.score}分
                        </span>
                      )}
                    </h3>
                    
                    {/* 总体评价 */}
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-3 last:mb-0 text-blue-800 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-blue-800">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-blue-800">{children}</ol>,
                          li: ({ children }) => <li className="text-blue-800">{children}</li>,
                          h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-blue-900">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-blue-900">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-blue-900">{children}</h3>,
                          strong: ({ children }) => <strong className="font-semibold text-blue-900">{children}</strong>,
                          em: ({ children }) => <em className="italic text-blue-800">{children}</em>,
                          code: ({ children }) => <code className="bg-blue-200 px-2 py-1 rounded text-sm font-mono text-blue-900">{children}</code>,
                          pre: ({ children }) => <pre className="bg-blue-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                          blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-400 pl-4 italic mb-3 text-blue-700">{children}</blockquote>
                        }}
                      >
                        {aiEvaluation}
                      </ReactMarkdown>
                    </div>
                    
                    {/* 结构化显示亮点和建议 */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* 亮点展示 */}
                      {evaluationData?.highlights && evaluationData.highlights.length > 0 && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                            <h4 className="font-semibold text-green-900">✨ 答案亮点</h4>
                          </div>
                          <ul className="space-y-2">
                            {evaluationData.highlights.map((highlight: string, index: number) => (
                              <li key={index} className="flex items-start text-green-800 text-sm">
                                <span className="text-green-600 mr-2 mt-1">•</span>
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* 改进建议 */}
                      {evaluationData?.suggestions && evaluationData.suggestions.length > 0 && (
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-center mb-3">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                            <h4 className="font-semibold text-orange-900">💡 改进建议</h4>
                          </div>
                          <ul className="space-y-2">
                            {evaluationData.suggestions.map((suggestion: string, index: number) => (
                              <li key={index} className="flex items-start text-orange-800 text-sm">
                                <span className="text-orange-600 mr-2 mt-1">•</span>
                                <span>{suggestion}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* 评分详情 */}
                    {evaluationData?.score && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">📊 综合评分</h4>
                          <div className="flex items-center">
                            <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                              <div 
                                className={`h-2 rounded-full transition-all duration-500 ${
                                  evaluationData.score >= 90 ? 'bg-green-500' :
                                  evaluationData.score >= 80 ? 'bg-blue-500' :
                                  evaluationData.score >= 70 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${evaluationData.score}%` }}
                              ></div>
                            </div>
                            <span className="text-lg font-bold text-gray-900">{evaluationData.score}/100</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {evaluationData.score >= 90 ? '优秀！您的答案展现了深度思考和卓越的分析能力。' :
                           evaluationData.score >= 80 ? '良好！您的思路清晰，分析较为全面。' :
                           evaluationData.score >= 70 ? '及格！基本掌握了要点，还有提升空间。' :
                           '需要改进！建议重新思考问题的核心要点。'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Your Answer */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                      你的答案
                    </h3>
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {answer}
                    </div>
                  </div>

                  {/* Reference Answer */}
                  <div className="bg-green-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2" />
                      参考答案
                    </h3>
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-3 last:mb-0 text-green-800 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-green-800">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-green-800">{children}</ol>,
                          li: ({ children }) => <li className="text-green-800">{children}</li>,
                          h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-green-900">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-green-900">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-green-900">{children}</h3>,
                          strong: ({ children }) => <strong className="font-semibold text-green-900">{children}</strong>,
                          em: ({ children }) => <em className="italic text-green-800">{children}</em>,
                          code: ({ children }) => <code className="bg-green-200 px-2 py-1 rounded text-sm font-mono text-green-900">{children}</code>,
                          pre: ({ children }) => <pre className="bg-green-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                          blockquote: ({ children }) => <blockquote className="border-l-4 border-green-400 pl-4 italic mb-3 text-green-700">{children}</blockquote>
                        }}
                      >
                        {selectedQuestion.ai_reference_answer}
                      </ReactMarkdown>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={resetQuestion}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      继续训练
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Questions List */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredQuestions.map((question) => (
              <div
                key={question.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedQuestion(question)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">
                      {question.title}
                    </h3>
                    <Play className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {question.thinking_type_tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {question.topic_tags.slice(0, 1).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    建议用时: 15-30分钟
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Training History Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    训练历史记录
                  </h2>
                  <button
                    onClick={() => {
                      setShowHistory(false);
                      setSelectedHistoryRecord(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : trainingHistory.length > 0 ? (
                  <div className="space-y-4">
                    {trainingHistory.map((record) => (
                      <div
                        key={record.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedHistoryRecord(record)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                              record.score >= 90 ? 'bg-green-100 text-green-800' :
                              record.score >= 80 ? 'bg-blue-100 text-blue-800' :
                              record.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {record.score}分
                            </div>
                            <span className="text-sm text-gray-600">
                              {new Date(record.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {record.text_answer}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">暂无训练记录</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Record Detail Modal */}
        {selectedHistoryRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    训练记录详情
                  </h2>
                  <button
                    onClick={() => setSelectedHistoryRecord(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* 题目信息 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">题目</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">
                        {selectedHistoryRecord.training_questions.title}
                      </h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedHistoryRecord.training_questions.content}
                      </div>
                    </div>
                  </div>

                  {/* 我的答案 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">我的答案</h3>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-blue-800 whitespace-pre-wrap">
                        {selectedHistoryRecord.text_answer}
                      </div>
                    </div>
                  </div>

                  {/* AI评价 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                      AI评价
                      <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${
                        selectedHistoryRecord.score >= 90 ? 'bg-green-100 text-green-800' :
                        selectedHistoryRecord.score >= 80 ? 'bg-blue-100 text-blue-800' :
                        selectedHistoryRecord.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedHistoryRecord.score}分
                      </span>
                    </h3>
                    <div className="p-4 bg-purple-50 rounded-lg prose max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-3 last:mb-0 text-purple-800 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-purple-800">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-purple-800">{children}</ol>,
                          li: ({ children }) => <li className="text-purple-800">{children}</li>,
                          h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-purple-900">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-purple-900">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-purple-900">{children}</h3>,
                          strong: ({ children }) => <strong className="font-semibold text-purple-900">{children}</strong>,
                          em: ({ children }) => <em className="italic text-purple-800">{children}</em>,
                          code: ({ children }) => <code className="bg-purple-200 px-2 py-1 rounded text-sm font-mono text-purple-900">{children}</code>,
                          pre: ({ children }) => <pre className="bg-purple-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                          blockquote: ({ children }) => <blockquote className="border-l-4 border-purple-400 pl-4 italic mb-3 text-purple-700">{children}</blockquote>
                        }}
                      >
                        {selectedHistoryRecord.ai_evaluation}
                      </ReactMarkdown>
                    </div>
                  </div>

                  {/* 参考答案 */}
                  {selectedHistoryRecord.training_questions.ai_reference_answer && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">参考答案</h3>
                      <div className="p-4 bg-green-50 rounded-lg prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 text-green-800 leading-relaxed">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-green-800">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-green-800">{children}</ol>,
                            li: ({ children }) => <li className="text-green-800">{children}</li>,
                            h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-green-900">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-green-900">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-green-900">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold text-green-900">{children}</strong>,
                            em: ({ children }) => <em className="italic text-green-800">{children}</em>,
                            code: ({ children }) => <code className="bg-green-200 px-2 py-1 rounded text-sm font-mono text-green-900">{children}</code>,
                            pre: ({ children }) => <pre className="bg-green-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-green-400 pl-4 italic mb-3 text-green-700">{children}</blockquote>,
                          }}
                        >
                          {selectedHistoryRecord.training_questions.ai_reference_answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* 训练时间 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">训练时间</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center text-gray-700">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(selectedHistoryRecord.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setSelectedHistoryRecord(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredQuestions.length === 0 && !loading && (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              暂无训练题目
            </h3>
            <p className="text-gray-600">
              当前筛选条件下没有找到训练题目
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Training;